import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardTitle,
  Container,
  Input,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Table,
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import FiltersPanel, { SelectOption } from "./components/FiltersPanel";
import { requestJson } from "../../../helpers/api";
import ControlCajaPanel, { GrupoContexto, MonchisTesoreriaMonto } from "./components/ControlCajaPanel";
import RegistroComprasStepper from "./components/RegistroComprasStepper";
import DepositoBoletaForm from "./components/DepositoBoletaForm";
import ConciliarPlataformaPanel from "./components/ConciliarPlataformaPanel";
import MonchisConciliacionPanel from "./components/MonchisConciliacionPanel";
import PlataformasSincronizacionPanel from "./components/PlataformasSincronizacionPanel";
import type { CajaDiaAnterior, SucursalRendicion } from "../RendicionCaja/types";
import StepArrowNav from "../RendicionCaja/components/StepArrowNav";
import StepLayout from "../RendicionCaja/components/StepLayout";
import { WizardStep } from "../RendicionCaja/constants";
import {
  FilaPorFormaPago,
  FilaReporteTesoreria,
  FiltroTesoreriaRegistro,
  FormaPagoDisponible,
  RendicionEstadoCaja,
} from "./types";

const ESTADO_OPCIONES: SelectOption[] = [
  { value: "", label: "Todos los estados" },
  { value: "diferencias", label: "Diferencias detectadas" },
  { value: "sin_diferencias", label: "Sin diferencias" },
  { value: "sobrantes", label: "Sobrantes en caja" },
];

type TabId = "sincronizacion" | "control" | "reportes" | "depositos" | "plataformas";

const TAB_LABELS: Record<TabId, string> = {
  sincronizacion: "Sincronización con sistema de gestión",
  control: "Control y verificación de cajas",
  reportes: "Registro de Facturas de Compras",
  depositos: "Registro de Depósitos Bancarios",
  plataformas: "Conciliación de Plataformas",
};

const TAB_DESCRIPTIONS: Record<TabId, string> = {
  sincronizacion: "Sincronizá datos del sistema de gestión con BaconApp.",
  control: "Validá las cajas y resolvé diferencias detectadas.",
  reportes: "Registrá las facturas de compras y sus soportes.",
  depositos: "Informá los depósitos bancarios para cerrar el turno.",
  plataformas: "Conciliá las plataformas externas y compará sus montos.",
};

const TAB_ORDER: TabId[] = ["sincronizacion", "reportes", "depositos", "plataformas", "control"];

const TAB_NAV_META: Record<TabId, { navLabel: string; navDescription: string }> = {
  sincronizacion: { navLabel: "Sincronización", navDescription: "Sistema de gestión" },
  reportes: { navLabel: "Facturas compras", navDescription: "Registros declarados" },
  depositos: { navLabel: "Depósitos bancarios", navDescription: "Declaraciones" },
  plataformas: { navLabel: "Plataformas", navDescription: "Coincidencias sugeridas" },
  control: { navLabel: "Control de cajas", navDescription: "Verificación final" },
};

const CONCILIACION_WIZARD_STEPS: Array<WizardStep & { tabId: TabId }> = TAB_ORDER.map((tabId) => {
  const meta = TAB_NAV_META[tabId];
  return {
    id: tabId,
    tabId,
    label: TAB_LABELS[tabId],
    navLabel: meta.navLabel,
    navDescription: meta.navDescription,
  };
});

type PlataformaIntegracionId = "monchis" | "pedidosya" | "bancard" | "ueno";

type PlataformaPlataformasTabId = "sincronizacion" | PlataformaIntegracionId;

const PLATAFORMAS_TAB_LABELS: Record<PlataformaPlataformasTabId, string> = {
  sincronizacion: "Sincronización",
  monchis: "Monchis conciliación",
  pedidosya: "PedidosYa conciliación",
  bancard: "Bancard conciliación",
  ueno: "Ueno / Upay conciliación",
};

const PLATAFORMAS_TAB_ORDER: PlataformaPlataformasTabId[] = [
  "sincronizacion",
  "monchis",
  "pedidosya",
  "bancard",
  "ueno",
];

type PlataformaIntegracionConfig = {
  id: PlataformaIntegracionId;
  titulo: string;
  panelProps: React.ComponentProps<typeof ConciliarPlataformaPanel>;
};

const INTEGRACIONES_PLATAFORMAS: PlataformaIntegracionConfig[] = [
  {
    id: "monchis",
    titulo: PLATAFORMAS_TAB_LABELS.monchis,
    panelProps: {
      titulo: "Conciliar Monchis",
      descripcion:
        "Procesá la planilla exportada de Monchis para comparar sus movimientos con las cajas del sistema.",
      endpoint: "/api/tesoreria/importaciones/monchis",
      archivoLabel: "Archivo Monchis",
      archivoAyuda: "Subí el archivo XLSX/XLS/CSV que provee Monchis.",
      botonTexto: "Procesar Monchis",
    },
  },
  {
    id: "pedidosya",
    titulo: PLATAFORMAS_TAB_LABELS.pedidosya,
    panelProps: {
      titulo: "Conciliar PedidosYa",
      descripcion:
        "Carga los reportes de PedidosYa y cruzalos contra las cajas para detectar diferencias.",
      endpoint: "/api/tesoreria/importaciones/pedidosya",
      archivoLabel: "Reporte PedidosYa",
      archivoAyuda: "Acepta archivos exportados desde el panel financiero de PedidosYa.",
      botonTexto: "Procesar PedidosYa",
    },
  },
  {
    id: "bancard",
    titulo: PLATAFORMAS_TAB_LABELS.bancard,
    panelProps: {
      titulo: "Conciliar Bancard",
      descripcion:
        "Subí los extractos de Bancard para validar los depósitos registrados en el sistema.",
      endpoint: "/api/tesoreria/importaciones/bancard",
      archivoLabel: "Extracto Bancard",
      archivoAyuda: "Adjuntá los archivos XLSX/XLS/CSV descargados desde Bancard.",
      botonTexto: "Procesar Bancard",
    },
  },
  {
    id: "ueno",
    titulo: PLATAFORMAS_TAB_LABELS.ueno,
    panelProps: {
      titulo: "Conciliar Ueno / Upay",
      descripcion: "Importá los extractos de Ueno o Upay para comparar los ingresos declarados contra Tesorería.",
      endpoint: "/api/tesoreria/importaciones/ueno",
      archivoLabel: "Extracto Ueno / Upay",
      archivoAyuda: "Adjuntá los archivos exportados desde la plataforma financiera de Ueno o Upay.",
      botonTexto: "Procesar extracto",
    },
  },
];

const construirFechaIso = (offsetDias: number) => {
  const fecha = new Date();
  fecha.setDate(fecha.getDate() + offsetDias);
  return fecha.toISOString().slice(0, 10);
};

const formatFechaCorta = (valor?: string | null) => {
  if (!valor) return "—";
  const fecha = new Date(valor);
  if (Number.isNaN(fecha.getTime())) {
    return valor;
  }
  return fecha.toLocaleDateString("es-PY");
};

const formatHoraCorta = (valor?: string | null) => {
  if (!valor) return null;
  const fecha = new Date(valor);
  if (Number.isNaN(fecha.getTime())) {
    return null;
  }
  return fecha.toLocaleTimeString("es-PY", { hour: "2-digit", minute: "2-digit" });
};

const formatearNombreFuente = (valor?: string | null) => {
  if (!valor) return "";
  const texto = valor.replace(/_/g, " ").trim();
  return texto.length > 0 ? texto.toUpperCase() : valor;
};

const normalizarFormaPagoDescripcion = (valor: string | null | undefined) => {
  if (!valor) return "";
  const texto = valor
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase();
  const sinPuntuacion = texto.replace(/[^A-Z0-9]/g, "");
  return sinPuntuacion.replace(/^[0-9]+/, "");
};
const buildGrupoKey = (fila: FilaPorFormaPago) =>
  `${fila.fuente ?? ""}|${fila.sucursal}|${fila.turno}|${fila.caja}|${fila.cajero}`;

type FacturasConciliadasResponse = {
  registros: FilaReporteTesoreria[];
  sincronizadoEn: string | null;
  sincronizadoPor: string | null;
};

const ConciliacionCajaPage = () => {
  const [periodoDesde, setPeriodoDesde] = useState(() => construirFechaIso(-1));
  const [periodoHasta, setPeriodoHasta] = useState(() => construirFechaIso(-1));
  const [opcionesSucursales, setOpcionesSucursales] = useState<SelectOption[]>([
    { value: "", label: "Todas las sucursales" },
  ]);
  const [opcionesServidores, setOpcionesServidores] = useState<SelectOption[]>([
    { value: "", label: "Todos los servidores" },
  ]);
  const [opcionesTurnos, setOpcionesTurnos] = useState<SelectOption[]>([{ value: "", label: "Todos los turnos" }]);
  const [opcionesCajas, setOpcionesCajas] = useState<SelectOption[]>([{ value: "", label: "Todas las cajas" }]);
  const [opcionesCajeros, setOpcionesCajeros] = useState<SelectOption[]>([{ value: "", label: "Todos los cajeros" }]);
  const [opcionesFormasPago, setOpcionesFormasPago] = useState<SelectOption[]>([{ value: "", label: "Todas las formas" }]);
  const [servidorSeleccionado, setServidorSeleccionado] = useState("");
  const [sucursalSeleccionada, setSucursalSeleccionada] = useState("");
  const [turnoSeleccionado, setTurnoSeleccionado] = useState("");
  const [cajaSeleccionada, setCajaSeleccionada] = useState("");
  const [cajeroSeleccionado, setCajeroSeleccionado] = useState("");
  const [estadoSeleccionado, setEstadoSeleccionado] = useState("");
  const [formaPagoSeleccionada, setFormaPagoSeleccionada] = useState("");
  const [cargandoFiltros, setCargandoFiltros] = useState(false);
  const [errorFiltros, setErrorFiltros] = useState<string | null>(null);
  const [tabActiva, setTabActiva] = useState<TabId>("control");
  const [plataformaTabActiva, setPlataformaTabActiva] = useState<PlataformaPlataformasTabId>("sincronizacion");
  const [reporte, setReporte] = useState<FilaReporteTesoreria[]>([]);
  const [filasPorFormaPagoBase, setFilasPorFormaPagoBase] = useState<FilaPorFormaPago[]>([]);
  const [cargandoReporte, setCargandoReporte] = useState(false);
  const [errorReporte, setErrorReporte] = useState<string | null>(null);
  const [rendicionesPorCaja, setRendicionesPorCaja] = useState<Record<number, RendicionEstadoCaja>>({});
  const [formasPagoManual, setFormasPagoManual] = useState<Record<string, FilaPorFormaPago[]>>({});
  const [filtrosControlAplicados, setFiltrosControlAplicados] = useState(() => ({
    periodoDesde: "",
    periodoHasta: "",
    fuente: "",
    sucursal: "",
    turno: "",
    caja: "",
    cajero: "",
    estado: "",
    formaPago: "",
  }));
  const [monchisTesoreriaMontos, setMonchisTesoreriaMontos] = useState<MonchisTesoreriaMonto[]>([]);
  const filasPorFormaPago = useMemo(() => {
    const manuales = Object.values(formasPagoManual).flat();
    return [...filasPorFormaPagoBase, ...manuales];
  }, [filasPorFormaPagoBase, formasPagoManual]);
  const [modalFacturas, setModalFacturas] = useState<{ visible: boolean; contexto: GrupoContexto | null }>({
    visible: false,
    contexto: null,
  });
  const [modalFacturasRegistros, setModalFacturasRegistros] = useState<FilaReporteTesoreria[]>([]);
  const [modalFacturasCargando, setModalFacturasCargando] = useState(false);
  const [modalFacturasError, setModalFacturasError] = useState<string | null>(null);
  const [modalFacturasTab, setModalFacturasTab] = useState<"todos" | "activas" | "anuladas">("todos");
  const [modalFacturasAgruparFormas, setModalFacturasAgruparFormas] = useState(true);
  const [modalFacturasSistemaHora, setModalFacturasSistemaHora] = useState<string | null>(null);
  const [modalFacturasSyncInfo, setModalFacturasSyncInfo] = useState<{ fecha: string | null; usuario: string | null }>(
    { fecha: null, usuario: null },
  );
  const [modalFormaPago, setModalFormaPago] = useState<{ visible: boolean; contexto: GrupoContexto | null }>({
    visible: false,
    contexto: null,
  });
  const [modalFacturasFormaDetalle, setModalFacturasFormaDetalle] = useState<{
    visible: boolean;
    forma: string | null;
    registros: FilaReporteTesoreria[];
  }>({ visible: false, forma: null, registros: [] });
  const [formasPagoDisponibles, setFormasPagoDisponibles] = useState<FormaPagoDisponible[]>([]);
  const [formasPagoCargando, setFormasPagoCargando] = useState(false);
  const [formasPagoError, setFormasPagoError] = useState<string | null>(null);
  const [formasPagoSeleccionadasIds, setFormasPagoSeleccionadasIds] = useState<Set<number>>(new Set());
  const [facturasPorCajaDetalle, setFacturasPorCajaDetalle] = useState<Record<number, FacturasConciliadasResponse>>({});
  const [filtrosVisibles, setFiltrosVisibles] = useState(false);
  const [cajasDisponibles, setCajasDisponibles] = useState<CajaDiaAnterior[]>([]);
  const [cargandoCajasDeposito, setCargandoCajasDeposito] = useState(false);
  const [errorCajasDeposito, setErrorCajasDeposito] = useState<string | null>(null);
  const [sucursalesDeposito, setSucursalesDeposito] = useState<SucursalRendicion[]>([]);
  const [cargandoSucursalesDeposito, setCargandoSucursalesDeposito] = useState(false);
  const [errorSucursalesDeposito, setErrorSucursalesDeposito] = useState<string | null>(null);
  const formasPagoActivas = useMemo(
    () => formasPagoDisponibles.filter((forma) => forma.estado === null || forma.estado === 1),
    [formasPagoDisponibles],
  );
  const formasExistentesNormalizadas = useMemo(() => {
    if (!modalFormaPago.contexto) {
      return new Set<string>();
    }
    return new Set(
      modalFormaPago.contexto.formasExistentes.map((forma) => normalizarFormaPagoDescripcion(forma)),
    );
  }, [modalFormaPago.contexto]);

  const permisosUsuario = useMemo(() => {
    try {
      const raw = sessionStorage.getItem("authUser");
      if (!raw) {
        return [];
      }
      const parsed = JSON.parse(raw);
      const lista:
        | string[]
        | undefined = Array.isArray(parsed?.usuario?.permisos)
        ? parsed.usuario.permisos
        : Array.isArray(parsed?.permisos)
        ? parsed.permisos
        : undefined;
      return Array.isArray(lista) ? lista.filter((permiso) => typeof permiso === "string") : [];
    } catch {
      return [];
    }
  }, []);

  const puedeConciliarSupervisor = permisosUsuario.includes("controlcaja.conciliacion_supervisor");
  const puedeConciliarTesoreria = permisosUsuario.includes("controlcaja.conciliacion_tesoreria");

  const tieneAcceso = puedeConciliarSupervisor || puedeConciliarTesoreria;
  const timelineSteps = useMemo(
    () => CONCILIACION_WIZARD_STEPS.map((step) => ({ ...step, clickable: true })),
    [],
  );
  const pasoActual = useMemo(() => {
    const idx = CONCILIACION_WIZARD_STEPS.findIndex((step) => step.tabId === tabActiva);
    return idx === -1 ? 0 : idx;
  }, [tabActiva]);
  const pasoActualStep = CONCILIACION_WIZARD_STEPS[pasoActual] ?? CONCILIACION_WIZARD_STEPS[0];
  const pasoActualId = pasoActualStep.tabId;
  const pasoDescripcion = TAB_DESCRIPTIONS[pasoActualId];
  const puedeRetroceder = pasoActual > 0;
  const puedeAvanzar = pasoActual < CONCILIACION_WIZARD_STEPS.length - 1;
  const handleStepSelection = useCallback(
    (index: number) => {
      if (index < 0 || index >= CONCILIACION_WIZARD_STEPS.length) {
        return;
      }
      const step = CONCILIACION_WIZARD_STEPS[index];
      if (!tieneAcceso) {
        setErrorReporte("No tenés permisos para ver este módulo.");
        return;
      }
      setTabActiva(step.tabId);
    },
    [tieneAcceso, setErrorReporte],
  );
  const retrocederPaso = useCallback(() => {
    if (puedeRetroceder) {
      handleStepSelection(pasoActual - 1);
    }
  }, [handleStepSelection, pasoActual, puedeRetroceder]);
  const avanzarPaso = useCallback(() => {
    if (puedeAvanzar) {
      handleStepSelection(pasoActual + 1);
    }
  }, [handleStepSelection, pasoActual, puedeAvanzar]);
  const resumenFiltros = useMemo(() => {
    const descripcionEstado =
      ESTADO_OPCIONES.find((opt) => opt.value === filtrosControlAplicados.estado)?.label ?? "Todos";
    const servidor =
      opcionesServidores.find((opt) => opt.value === filtrosControlAplicados.fuente)?.label ??
      "Todos los servidores";
    const sucursal =
      opcionesSucursales.find((opt) => opt.value === filtrosControlAplicados.sucursal)?.label ??
      "Todas las sucursales";
    return {
      periodo: `${filtrosControlAplicados.periodoDesde || "..."} al ${
        filtrosControlAplicados.periodoHasta || "..."
      }`,
      servidor,
      sucursal,
      estado: descripcionEstado,
    };
  }, [filtrosControlAplicados, opcionesSucursales, opcionesServidores]);

  const crearOpcionesUnicas = (registros: FiltroTesoreriaRegistro[], clave: keyof FiltroTesoreriaRegistro, labelKey: keyof FiltroTesoreriaRegistro, fallback: string) => {
    const mapa = new Map<string, string>();
    registros.forEach((registro) => {
      const valorBruto = registro[clave];
      if (valorBruto === null || valorBruto === undefined) {
        return;
      }
      const textoClave = String(valorBruto);
      const etiquetaBruta = registro[labelKey];
      const etiqueta =
        (typeof etiquetaBruta === "string" && etiquetaBruta.trim().length > 0
          ? etiquetaBruta
          : `${fallback} ${textoClave}`).trim();
      if (!mapa.has(textoClave)) {
        mapa.set(textoClave, etiqueta);
      }
    });
    return Array.from(mapa.entries()).map(([value, label]) => ({ value, label }));
  };

  const crearOpcionesSucursalesDesdeAsignadas = useCallback(
    (sucursales: SucursalRendicion[], fuenteSeleccionada?: string) => {
    if (!Array.isArray(sucursales) || sucursales.length === 0) {
      setOpcionesServidores([{ value: "", label: "Todos los servidores" }]);
      setOpcionesSucursales([{ value: "", label: "Todas las sucursales" }]);
      return;
    }
    const opcionesFuente = Array.from(
      new Map(
        sucursales
          .map((item) => item.fuente ?? "")
          .filter((fuente) => fuente.trim().length > 0)
          .map((fuente) => [fuente, formatearNombreFuente(fuente)]),
      ),
    ).map(([value, label]) => ({ value, label }));
    setOpcionesServidores([{ value: "", label: "Todos los servidores" }, ...opcionesFuente]);
    const sucursalesFiltradas = fuenteSeleccionada
      ? sucursales.filter((item) => (item.fuente ?? "").toLowerCase() === fuenteSeleccionada.toLowerCase())
      : sucursales;
    const opcionesSucursal = Array.from(
      new Map(
        sucursalesFiltradas
          .filter((item) => Number.isFinite(item.idsucursal))
          .map((item) => {
            const etiqueta = (item.sucursalNombre ?? "").trim() || `Sucursal ${item.idsucursal}`;
            return [String(item.idsucursal), etiqueta];
          }),
      ),
    ).map(([value, label]) => ({ value, label }));
    if (fuenteSeleccionada && opcionesSucursal.length === 0) {
      setOpcionesSucursales([{ value: "", label: "Sin sucursales para el servidor" }]);
      return;
    }
    setOpcionesSucursales([{ value: "", label: "Todas las sucursales" }, ...opcionesSucursal]);
  }, []);

  const cargarFiltros = useCallback(async () => {
    const { periodoDesde: periodoDesdeFiltro, periodoHasta: periodoHastaFiltro } = filtrosControlAplicados;
    if (!periodoDesdeFiltro || !periodoHastaFiltro || !tieneAcceso) {
      if (!tieneAcceso) {
        setOpcionesTurnos([{ value: "", label: "Todos los turnos" }]);
        setOpcionesCajas([{ value: "", label: "Todas las cajas" }]);
        setOpcionesCajeros([{ value: "", label: "Todos los cajeros" }]);
        setErrorFiltros("No tenés permisos para ver estos filtros.");
      } else {
        setOpcionesTurnos([{ value: "", label: "Todos los turnos" }]);
        setOpcionesCajas([{ value: "", label: "Todas las cajas" }]);
        setOpcionesCajeros([{ value: "", label: "Todos los cajeros" }]);
        setOpcionesFormasPago([{ value: "", label: "Todas las formas" }]);
      }
      return;
    }
    setCargandoFiltros(true);
    setErrorFiltros(null);
    try {
      const params = new URLSearchParams({ desde: periodoDesdeFiltro, hasta: periodoHastaFiltro });
      if (filtrosControlAplicados.fuente) {
        params.set("fuente", filtrosControlAplicados.fuente);
      }
      const registros = await requestJson<FiltroTesoreriaRegistro[]>(`/api/tesoreria/filtros?${params.toString()}`);
      const lista = (Array.isArray(registros) ? registros : []).filter((registro) => {
        if (filtrosControlAplicados.fuente) {
          const fuenteRegistro = (registro.fuente ?? "").toLowerCase();
          if (fuenteRegistro !== filtrosControlAplicados.fuente.toLowerCase()) {
            return false;
          }
        }
        if (filtrosControlAplicados.sucursal) {
          return String(registro.idsucursal ?? "") === filtrosControlAplicados.sucursal;
        }
        return true;
      });
      setOpcionesTurnos([{ value: "", label: "Todos los turnos" }, ...crearOpcionesUnicas(lista, "idturno", "turno", "Turno")]);
      setOpcionesCajas([{ value: "", label: "Todas las cajas" }, ...crearOpcionesUnicas(lista, "idcontrolcaja", "idcontrolcaja", "Caja")]);
      setOpcionesCajeros([
        { value: "", label: "Todos los cajeros" },
        ...lista.reduce<SelectOption[]>((acc, registro) => {
          const documento = registro.cajeroDocumento ?? "";
          const nombre = registro.cajero ?? "";
          const clave = (documento || nombre).trim();
          if (!clave || acc.some((item) => item.value === clave)) {
            return acc;
          }
          acc.push({ value: clave, label: nombre || documento || "Cajero sin nombre" });
          return acc;
        }, []),
      ]);
      setOpcionesFormasPago([
        { value: "", label: "Todas las formas" },
        ...crearOpcionesUnicas(lista, "idformaPago", "formaPago", "Forma"),
      ]);
    } catch (error) {
      setErrorFiltros(error instanceof Error ? error.message : "No se pudieron obtener los filtros.");
      setOpcionesTurnos([{ value: "", label: "Todos los turnos" }]);
      setOpcionesCajas([{ value: "", label: "Todas las cajas" }]);
      setOpcionesCajeros([{ value: "", label: "Todos los cajeros" }]);
      setOpcionesFormasPago([{ value: "", label: "Todas las formas" }]);
    } finally {
      setCargandoFiltros(false);
    }
  }, [filtrosControlAplicados, tieneAcceso]);

  const rangoDepositoRef = useRef<{ desde: string; hasta: string }>({
    desde: construirFechaIso(-7),
    hasta: construirFechaIso(0),
  });

  const cargarCajasDeposito = useCallback(async () => {
    if (!tieneAcceso) {
      if (!tieneAcceso) {
        setErrorCajasDeposito("No tenés permisos para listar las cajas.");
      }
      setCajasDisponibles([]);
      return;
    }
    setCargandoCajasDeposito(true);
    setErrorCajasDeposito(null);
    try {
      const params = new URLSearchParams({
        desde: rangoDepositoRef.current.desde,
        hasta: rangoDepositoRef.current.hasta,
      });
      const data = await requestJson<CajaDiaAnterior[]>(`/api/tesoreria/cajas-dia-anterior?${params.toString()}`);
      const lista = Array.isArray(data) ? data : [];
      if (lista.length === 0) {
        setCajasDisponibles([]);
      } else {
        const ids = lista
          .map((caja) => Number(caja.idcontrolcaja))
          .filter((id) => Number.isFinite(id) && id > 0);
        if (ids.length > 0) {
          try {
            const timestamp = Date.now().toString();
            const estados = await requestJson<RendicionEstadoCaja[]>(
              `/api/rendiciones/estados-cajas?ids=${ids.join(",")}&_=${timestamp}`,
            );
            const mapa = (Array.isArray(estados) ? estados : []).reduce<Record<number, RendicionEstadoCaja>>(
              (acc, registro) => {
                if (Number.isFinite(registro.idcontrolcaja)) {
                  acc[registro.idcontrolcaja] = registro;
                }
                return acc;
              },
              {},
            );
            setCajasDisponibles(
              lista.map((caja) => {
                const estado = caja.idcontrolcaja != null ? mapa[caja.idcontrolcaja] : undefined;
                return estado
                  ? {
                      ...caja,
                      estado: estado.estado ?? caja.estado ?? null,
                      descEstadoCaja: estado.estado ?? caja.descEstadoCaja ?? null,
                      descEstadoTurno: estado.supervisorEstado ?? caja.descEstadoTurno ?? null,
                    }
                  : caja;
              }),
            );
          } catch (estadoError) {
            console.warn("No pudimos obtener los estados de las cajas para depósitos.", estadoError);
            setCajasDisponibles(lista);
          }
        } else {
          setCajasDisponibles(lista);
        }
      }
    } catch (error) {
      setErrorCajasDeposito(error instanceof Error ? error.message : "No pudimos obtener las cajas disponibles.");
      setCajasDisponibles([]);
    } finally {
      setCargandoCajasDeposito(false);
    }
  }, [tieneAcceso]);

  const cargarSucursalesDeposito = useCallback(async () => {
    if (!tieneAcceso) {
      setSucursalesDeposito([]);
      setErrorSucursalesDeposito("No tenés permisos para listar sucursales.");
      return;
    }
    setCargandoSucursalesDeposito(true);
    setErrorSucursalesDeposito(null);
    try {
      const data = await requestJson<SucursalRendicion[]>("/api/tesoreria/mis-sucursales");
      setSucursalesDeposito(Array.isArray(data) ? data : []);
    } catch (error) {
      setErrorSucursalesDeposito(error instanceof Error ? error.message : "No pudimos obtener tus sucursales.");
      setSucursalesDeposito([]);
    } finally {
      setCargandoSucursalesDeposito(false);
    }
  }, [tieneAcceso]);

  useEffect(() => {
    crearOpcionesSucursalesDesdeAsignadas(sucursalesDeposito, servidorSeleccionado);
  }, [crearOpcionesSucursalesDesdeAsignadas, sucursalesDeposito, servidorSeleccionado]);

  useEffect(() => {
    if (!servidorSeleccionado) {
      if (sucursalSeleccionada) {
        setSucursalSeleccionada("");
      }
      return;
    }
    if (!sucursalSeleccionada) {
      return;
    }
    const match = sucursalesDeposito.some(
      (item) =>
        String(item.idsucursal) === sucursalSeleccionada &&
        (item.fuente ?? "").toLowerCase() === servidorSeleccionado.toLowerCase(),
    );
    if (!match) {
      setSucursalSeleccionada("");
    }
  }, [servidorSeleccionado, sucursalSeleccionada, sucursalesDeposito]);

  useEffect(() => {
    void cargarFiltros();
  }, [cargarFiltros]);
  useEffect(() => {
    void cargarCajasDeposito();
  }, [cargarCajasDeposito]);
  useEffect(() => {
    void cargarSucursalesDeposito();
  }, [cargarSucursalesDeposito]);

  const toggleFiltros = useCallback(() => {
    setFiltrosVisibles((prev) => !prev);
  }, []);
  const manejarActualizarFiltros = useCallback(() => {
    if (!periodoDesde || !periodoHasta) {
      setErrorFiltros("Seleccioná un rango de fechas válido.");
      return;
    }
    setFiltrosControlAplicados({
      periodoDesde,
      periodoHasta,
      fuente: servidorSeleccionado,
      sucursal: sucursalSeleccionada,
      turno: turnoSeleccionado,
      caja: cajaSeleccionada,
      cajero: cajeroSeleccionado,
      estado: estadoSeleccionado,
      formaPago: formaPagoSeleccionada,
    });
    setFiltrosVisibles(false);
  }, [
    periodoDesde,
    periodoHasta,
    servidorSeleccionado,
    sucursalSeleccionada,
    turnoSeleccionado,
    cajaSeleccionada,
    cajeroSeleccionado,
    estadoSeleccionado,
    formaPagoSeleccionada,
  ]);

  const construirUrlFacturasConciliadas = useCallback(
    (cajaId?: number | null) => {
      if (
        !cajaId ||
        !Number.isFinite(cajaId) ||
        cajaId <= 0 ||
        !filtrosControlAplicados.periodoDesde ||
        !filtrosControlAplicados.periodoHasta
      ) {
        return null;
      }
      const params = new URLSearchParams({
        desde: filtrosControlAplicados.periodoDesde,
        hasta: filtrosControlAplicados.periodoHasta,
      });
      return `/api/tesoreria/cajas/${cajaId}/registros-conciliados?${params.toString()}`;
    },
    [filtrosControlAplicados.periodoDesde, filtrosControlAplicados.periodoHasta],
  );

  const cargarFacturasGrupo = useCallback(
    async (contexto: GrupoContexto) => {
      if (!contexto.idcontrolcaja) {
        setModalFacturasRegistros([]);
        setModalFacturasError("No identificamos la caja seleccionada.");
        setModalFacturasCargando(false);
        return;
      }
      if (facturasPorCajaDetalle[contexto.idcontrolcaja]) {
        const existente = facturasPorCajaDetalle[contexto.idcontrolcaja];
        setModalFacturasRegistros(existente.registros);
        setModalFacturasSyncInfo({ fecha: existente.sincronizadoEn, usuario: existente.sincronizadoPor });
        setModalFacturasError(null);
        setModalFacturasCargando(false);
        return;
      }
      const url = construirUrlFacturasConciliadas(contexto.idcontrolcaja);
      if (!url) {
        setModalFacturasRegistros([]);
        setModalFacturasError("Seleccioná un rango de fechas para ver las facturas del grupo.");
        setModalFacturasCargando(false);
        return;
      }
      setModalFacturasCargando(true);
      setModalFacturasError(null);
      try {
        const data = await requestJson<FacturasConciliadasResponse>(url);
        const registros = Array.isArray((data as any)?.registros) ? data.registros : [];
        setFacturasPorCajaDetalle((prev) => ({
          ...prev,
          [contexto.idcontrolcaja as number]: {
            registros,
            sincronizadoEn: data.sincronizadoEn ?? null,
            sincronizadoPor: data.sincronizadoPor ?? null,
          },
        }));
        setModalFacturasRegistros(registros);
        setModalFacturasSyncInfo({ fecha: data.sincronizadoEn ?? null, usuario: data.sincronizadoPor ?? null });
      } catch (error) {
        setModalFacturasError(
          error instanceof Error
            ? error.message
            : "No se pudieron obtener las facturas conciliadas para esta caja.",
        );
        setModalFacturasRegistros([]);
        setModalFacturasSyncInfo({ fecha: null, usuario: null });
      } finally {
        setModalFacturasCargando(false);
      }
    },
    [construirUrlFacturasConciliadas, facturasPorCajaDetalle],
  );

  const cargarFormasPago = useCallback(async () => {
    try {
      setFormasPagoCargando(true);
      setFormasPagoError(null);
      const data = await requestJson<FormaPagoDisponible[]>("/api/tesoreria/formas-pago");
      if (Array.isArray(data)) {
        setFormasPagoDisponibles(data);
      } else {
        setFormasPagoDisponibles([]);
      }
    } catch (error) {
      setFormasPagoError(error instanceof Error ? error.message : "No se pudieron cargar las formas de pago.");
    } finally {
      setFormasPagoCargando(false);
    }
  }, []);

  const handleAgregarFormaPago = useCallback(
    (contexto: GrupoContexto) => {
    setModalFormaPago({ visible: true, contexto });
    setFormasPagoSeleccionadasIds(new Set());
      setFormasPagoError(null);
      if (formasPagoDisponibles.length === 0) {
        void cargarFormasPago();
      }
    },
    [formasPagoDisponibles.length, cargarFormasPago],
  );

  const handleVerFacturas = useCallback(
    (contexto: GrupoContexto) => {
      setModalFacturas({ visible: true, contexto });
      setModalFacturasTab("todos");
      setModalFacturasAgruparFormas(true);
      setModalFacturasSistemaHora(
        new Date().toLocaleTimeString("es-PY", {
          hour: "2-digit",
          minute: "2-digit",
        }),
      );
      if (contexto.idcontrolcaja && facturasPorCajaDetalle[contexto.idcontrolcaja]) {
        const data = facturasPorCajaDetalle[contexto.idcontrolcaja];
        setModalFacturasRegistros(data.registros);
        setModalFacturasSyncInfo({ fecha: data.sincronizadoEn, usuario: data.sincronizadoPor });
        setModalFacturasError(null);
        setModalFacturasCargando(false);
      } else {
        setModalFacturasRegistros([]);
        setModalFacturasSyncInfo({ fecha: null, usuario: null });
        setModalFacturasError(null);
        setModalFacturasCargando(true);
      }
      void cargarFacturasGrupo(contexto);
    },
    [cargarFacturasGrupo, facturasPorCajaDetalle],
  );

  const cerrarModalFacturas = () => {
    setModalFacturas({ visible: false, contexto: null });
    setModalFacturasRegistros([]);
    setModalFacturasError(null);
    setModalFacturasCargando(false);
    setModalFacturasTab("todos");
    setModalFacturasAgruparFormas(true);
    setModalFacturasFormaDetalle({ visible: false, forma: null, registros: [] });
    setModalFacturasSistemaHora(null);
    setModalFacturasSyncInfo({ fecha: null, usuario: null });
  };
  const cerrarModalFormaPago = () => {
    setModalFormaPago({ visible: false, contexto: null });
    setFormasPagoSeleccionadasIds(new Set());
    setFormasPagoError(null);
  };

  const abrirModalFacturasForma = useCallback((forma: string, registros: FilaReporteTesoreria[]) => {
    setModalFacturasFormaDetalle({
      visible: true,
      forma,
      registros,
    });
  }, []);

  const cerrarModalFacturasForma = () => {
    setModalFacturasFormaDetalle({ visible: false, forma: null, registros: [] });
  };

  const modalFacturasResumen = useMemo(() => {
    const total = modalFacturasRegistros.length;
    const anuladas = modalFacturasRegistros.filter(
      (fila) => String(fila.tipoFactura ?? "").toUpperCase() === "ANULADA",
    ).length;
    const activas = total - anuladas;
    const sumatoria = (registros: FilaReporteTesoreria[]) =>
      registros.reduce((acc, fila) => acc + Number(fila.monto ?? 0), 0);
    return {
      total,
      activas,
      anuladas,
      sumatorias: {
        todos: sumatoria(modalFacturasRegistros),
        activas: sumatoria(
          modalFacturasRegistros.filter(
            (fila) => String(fila.tipoFactura ?? "").toUpperCase() !== "ANULADA",
          ),
        ),
        anuladas: sumatoria(
          modalFacturasRegistros.filter(
            (fila) => String(fila.tipoFactura ?? "").toUpperCase() === "ANULADA",
          ),
        ),
      },
    };
  }, [modalFacturasRegistros]);

  const modalFacturasFiltradas = useMemo(() => {
    let lista = modalFacturasRegistros;
    if (modalFacturasTab !== "todos") {
      const esAnulada = (fila: FilaReporteTesoreria) => String(fila.tipoFactura ?? "").toUpperCase() === "ANULADA";
      lista =
        modalFacturasTab === "activas"
          ? modalFacturasRegistros.filter((fila) => !esAnulada(fila))
          : modalFacturasRegistros.filter(esAnulada);
    }
    return [...lista].sort((a, b) => {
      const fa = String(a.nromovimiento ?? "").toUpperCase();
      const fb = String(b.nromovimiento ?? "").toUpperCase();
      if (fa < fb) return -1;
      if (fa > fb) return 1;
      return 0;
    });
  }, [modalFacturasRegistros, modalFacturasTab]);

  const modalFacturasPorFormaPago = useMemo(() => {
    const mapa = new Map<
      string,
      { descripcion: string; total: number; cantidad: number; registros: FilaReporteTesoreria[] }
    >();
    modalFacturasFiltradas.forEach((fila) => {
      const clave = (fila.formaPago ?? "Sin forma").trim() || "Sin forma";
      if (!mapa.has(clave)) {
        mapa.set(clave, { descripcion: clave, total: 0, cantidad: 0, registros: [] });
      }
      const item = mapa.get(clave)!;
      item.total += Number(fila.monto ?? 0);
      item.cantidad += 1;
      item.registros.push(fila);
    });
    return Array.from(mapa.values()).sort((a, b) => b.total - a.total);
  }, [modalFacturasFiltradas]);

  const obtenerMontoSistemaPorForma = useCallback(
    (descripcion: string) => {
      const contexto = modalFacturas.contexto;
      if (!contexto?.montosSistemaPorForma) {
        return null;
      }
      const clave = normalizarFormaPagoDescripcion(descripcion);
      return contexto.montosSistemaPorForma[clave]?.monto ?? null;
    },
    [modalFacturas.contexto],
  );

  const sistemaGestionTitulo = useMemo(
    () => ({
      titulo: "Sistema de gestión",
      subtitulo: `Recuperado en línea${modalFacturasSistemaHora ? ` · ${modalFacturasSistemaHora}` : ""}`,
    }),
    [modalFacturasSistemaHora],
  );

  const sincronizadosLabel = useMemo(() => {
    const hora = formatHoraCorta(modalFacturasSyncInfo.fecha);
    const usuario = modalFacturasSyncInfo.usuario;
    let subtitulo = "Sincronización pendiente";
    if (usuario || hora) {
      const partes: string[] = [];
      if (usuario) {
        partes.push(usuario);
      }
      if (hora) {
        partes.push(hora);
      }
      subtitulo = `Por ${partes.join(" · ")}`;
    }
    return {
      titulo: "Sincronizados con BaconApp",
      subtitulo,
    };
  }, [modalFacturasSyncInfo]);

  const totalSistemaAgrupado = useMemo(() => {
    return modalFacturasPorFormaPago.reduce(
      (acc, grupo) => acc + (obtenerMontoSistemaPorForma(grupo.descripcion) ?? 0),
      0,
    );
  }, [modalFacturasPorFormaPago, obtenerMontoSistemaPorForma]);

  const totalFacturasAgrupado = useMemo(() => {
    return modalFacturasPorFormaPago.reduce((acc, grupo) => acc + grupo.total, 0);
  }, [modalFacturasPorFormaPago]);

  const construirFilasPorFormaPago = useCallback((filas: FilaReporteTesoreria[]): FilaPorFormaPago[] => {
    const mapa = new Map<string, FilaPorFormaPago>();
    filas.forEach((fila) => {
      const tipoFactura = String(fila.tipoFactura ?? "").toUpperCase();
      if (tipoFactura === "ANULADA") {
        return;
      }
      const sucursal = fila.sucursal ?? (fila.idsucursal != null ? `Sucursal ${fila.idsucursal}` : "Sin sucursal");
      const turno = fila.turno ?? (fila.idturno != null ? `Turno ${fila.idturno}` : "Sin turno");
      const caja = fila.idcontrolcaja != null ? `Caja ${fila.idcontrolcaja}` : "Sin caja";
      const cajero = fila.cajero ?? "Sin cajero";
      const formaPago = fila.formaPago ?? (tipoFactura === "CREDITO" ? "CRÉDITO" : "SIN FORMA");
      const fuente = fila.fuente ?? null;
      const key = `${fuente ?? ""}|${sucursal}|${turno}|${caja}|${cajero}|${formaPago}`;
      const existente = mapa.get(key);
      if (existente) {
        existente.monto += fila.monto;
      } else {
        mapa.set(key, {
          id: key,
          idsucursal: fila.idsucursal,
          idturno: fila.idturno,
          idcontrolcaja: fila.idcontrolcaja,
          idformaPago: fila.idformaPago ?? null,
          idcajero: fila.idcajero,
          fuente,
          sucursal,
          turno,
          caja,
          cajero,
          formaPago,
          monto: fila.monto,
          descEstadoTurno: fila.descEstadoTurno ?? null,
          descEstadoCaja: fila.descEstadoCaja ?? null,
          fechaHoraAperturaTurno: fila.fechaHoraAperturaTurno ?? null,
          fechaApertura: fila.fechaApertura ?? null,
          fechaCierre: fila.fechaCierre ?? null,
        });
      }
    });
    return Array.from(mapa.values());
  }, []);

  const confirmarAgregarFormaPago = useCallback(() => {
    if (!modalFormaPago.contexto || formasPagoSeleccionadasIds.size === 0) {
      return;
    }
    const contexto = modalFormaPago.contexto;
    const nuevasFormas: FilaPorFormaPago[] = [];
    const nuevosIds = new Set(formasPagoSeleccionadasIds);
    nuevosIds.forEach((idSeleccionado) => {
      const seleccionada = formasPagoDisponibles.find((forma) => forma.id === idSeleccionado);
      if (!seleccionada) {
        return;
      }
      const descripcionBase = (seleccionada.descripcion ?? "").trim();
      const descripcion = descripcionBase.length > 0 ? descripcionBase : "SIN DESCRIPCIÓN";
      const normalizada = normalizarFormaPagoDescripcion(descripcion);
      if (formasExistentesNormalizadas.has(normalizada)) {
        return;
      }
      const nuevoRegistro: FilaPorFormaPago = {
        id: `MANUAL:${contexto.key}:${seleccionada.id}:${Date.now()}:${Math.random()}`,
        idsucursal: contexto.idsucursal,
        idturno: contexto.idturno,
        idcontrolcaja: contexto.idcontrolcaja,
        idcajero: contexto.idcajero,
        idformaPago: seleccionada.id,
        fuente: contexto.fuente,
        sucursal: contexto.sucursal,
        turno: contexto.turno,
        caja: contexto.caja,
        cajero: contexto.cajero,
        formaPago: descripcion,
        monto: 0,
        descEstadoTurno: contexto.descEstadoTurno,
        descEstadoCaja: contexto.descEstadoCaja,
        fechaHoraAperturaTurno: contexto.fechaHoraAperturaTurno,
        fechaApertura: contexto.fechaApertura,
        fechaCierre: contexto.fechaCierre,
        esManual: true,
        claveManual: contexto.key,
      };
      nuevasFormas.push(nuevoRegistro);
    });
    if (nuevasFormas.length === 0) {
      setFormasPagoError("Ninguna de las formas seleccionadas está disponible.");
      return;
    }
    setFormasPagoManual((prev) => {
      const lista = prev[contexto.key] ?? [];
      return {
        ...prev,
        [contexto.key]: [...lista, ...nuevasFormas],
      };
    });
    setModalFormaPago({ visible: false, contexto: null });
    setFormasPagoSeleccionadasIds(new Set());
    setFormasPagoError(null);
  }, [
    modalFormaPago.contexto,
    formasPagoSeleccionadasIds,
    formasPagoDisponibles,
    formasExistentesNormalizadas,
  ]);

  const manejarEliminarFormaManual = useCallback((fila: FilaPorFormaPago) => {
    if (!fila.esManual || !fila.claveManual) {
      return;
    }
    setFormasPagoManual((prev) => {
      const lista = prev[fila.claveManual as string];
      if (!lista) {
        return prev;
      }
      const nuevaLista = lista.filter((item) => item.id !== fila.id);
      if (nuevaLista.length === lista.length) {
        return prev;
      }
      const actualizado = { ...prev };
      if (nuevaLista.length === 0) {
        delete actualizado[fila.claveManual as string];
      } else {
        actualizado[fila.claveManual as string] = nuevaLista;
      }
      return actualizado;
    });
  }, []);

  const cargarReporte = useCallback(async () => {
    const {
      periodoDesde: periodoDesdeFiltro,
      periodoHasta: periodoHastaFiltro,
      fuente: fuenteFiltro,
      sucursal: sucursalFiltro,
      turno: turnoFiltro,
      caja: cajaFiltro,
      cajero: cajeroFiltro,
      estado: estadoFiltro,
      formaPago,
    } = filtrosControlAplicados;
    if (!periodoDesdeFiltro || !periodoHastaFiltro) {
      return;
    }
    if (!tieneAcceso) {
      setErrorReporte("No tenés permisos para ver este reporte.");
      setReporte([]);
      setFilasPorFormaPagoBase([]);
      setFormasPagoManual({});
      return;
    }
    setCargandoReporte(true);
    setErrorReporte(null);
    try {
      const params = new URLSearchParams({
        desde: periodoDesdeFiltro,
        hasta: periodoHastaFiltro,
      });
      if (fuenteFiltro) params.set("fuente", fuenteFiltro);
      if (sucursalFiltro) params.set("sucursal", sucursalFiltro);
      if (turnoFiltro) params.set("turno", turnoFiltro);
      if (cajaFiltro) params.set("caja", cajaFiltro);
      if (cajeroFiltro) params.set("cajero", cajeroFiltro);
      if (estadoFiltro) params.set("estado", estadoFiltro);
      if (formaPago) params.set("formaPago", formaPago);
      const data = await requestJson<FilaReporteTesoreria[]>(`/api/tesoreria/reporte?${params.toString()}`);
      const filas = Array.isArray(data) ? data : [];
      setReporte(filas);
      const construidas = construirFilasPorFormaPago(filas);
      setFilasPorFormaPagoBase(construidas);
      setFormasPagoManual((prev) => {
        if (Object.keys(prev).length === 0) {
          return prev;
        }
        const clavesValidas = new Set(construidas.map((fila) => buildGrupoKey(fila)));
        const actualizado = Object.entries(prev).reduce<Record<string, FilaPorFormaPago[]>>((acc, [clave, lista]) => {
          if (clavesValidas.has(clave)) {
            acc[clave] = lista;
          }
          return acc;
        }, {});
        if (Object.keys(actualizado).length === Object.keys(prev).length) {
          return prev;
        }
        return actualizado;
      });
    } catch (error) {
      setErrorReporte(error instanceof Error ? error.message : "No se pudo obtener el reporte.");
      setReporte([]);
      setFilasPorFormaPagoBase([]);
      setFormasPagoManual({});
    } finally {
      setCargandoReporte(false);
    }
  }, [construirFilasPorFormaPago, filtrosControlAplicados, tieneAcceso]);

  useEffect(() => {
    void cargarReporte();
  }, [cargarReporte]);

  const idsRendiciones = useMemo(() => {
    return Array.from(
      new Set(
        filasPorFormaPago
          .map((fila) => (typeof fila.idcontrolcaja === "number" ? fila.idcontrolcaja : null))
          .filter((valor): valor is number => valor != null && Number.isFinite(valor) && valor > 0),
      ),
    );
  }, [filasPorFormaPago]);

  const cargarRendiciones = useCallback(
    async (opciones?: { signal?: AbortSignal }) => {
      if (!tieneAcceso) {
        setRendicionesPorCaja({});
        return;
      }
      if (idsRendiciones.length === 0) {
        setRendicionesPorCaja({});
        return;
      }
      try {
        const timestamp = Date.now();
        const data = await requestJson<RendicionEstadoCaja[]>(
          `/api/rendiciones/estados-cajas?ids=${idsRendiciones.join(",")}&_=${timestamp}`,
          { signal: opciones?.signal },
        );
        const mapa = (Array.isArray(data) ? data : []).reduce<Record<number, RendicionEstadoCaja>>((acc, registro) => {
          const key = Number(registro.idcontrolcaja);
          if (Number.isFinite(key) && key > 0) {
            acc[key] = registro;
          }
          return acc;
        }, {});
        setRendicionesPorCaja(mapa);
      } catch (error) {
        if (opciones?.signal?.aborted) {
          return;
        }
        console.warn("No se pudieron obtener las rendiciones por caja", error);
      }
    },
    [idsRendiciones],
  );

  useEffect(() => {
    const abortController = new AbortController();
    void cargarRendiciones({ signal: abortController.signal });
    return () => {
      abortController.abort();
    };
  }, [cargarRendiciones]);

  useEffect(() => {
    if (
      !filtrosControlAplicados.periodoDesde ||
      !filtrosControlAplicados.periodoHasta ||
      idsRendiciones.length === 0 ||
      !tieneAcceso
    ) {
      setFacturasPorCajaDetalle({});
      return;
    }
    let cancelado = false;
    const idsOrdenados = [...idsRendiciones].sort((a, b) => b - a);
    const cargar = async () => {
      const acumulado: Record<number, FacturasConciliadasResponse> = {};
      for (const id of idsOrdenados) {
        const url = construirUrlFacturasConciliadas(id);
        if (!url) {
          acumulado[id] = { registros: [], sincronizadoEn: null, sincronizadoPor: null };
          continue;
        }
        try {
          const data = await requestJson<FacturasConciliadasResponse>(url);
          acumulado[id] = {
            registros: Array.isArray((data as any)?.registros) ? data.registros : [],
            sincronizadoEn: data.sincronizadoEn ?? null,
            sincronizadoPor: data.sincronizadoPor ?? null,
          };
        } catch (error) {
          console.warn("No se pudieron cargar las facturas conciliadas para la caja", id, error);
          acumulado[id] = { registros: [], sincronizadoEn: null, sincronizadoPor: null };
        }
        if (cancelado) {
          return;
        }
      }
      if (!cancelado) {
        setFacturasPorCajaDetalle(acumulado);
      }
    };
    void cargar();
    return () => {
      cancelado = true;
    };
  }, [
    construirUrlFacturasConciliadas,
    filtrosControlAplicados.periodoDesde,
    filtrosControlAplicados.periodoHasta,
    idsRendiciones,
    tieneAcceso,
  ]);

  useEffect(() => {
    let cancelado = false;
    if (!tieneAcceso || idsRendiciones.length === 0) {
      setMonchisTesoreriaMontos([]);
      return;
    }
    const params = new URLSearchParams({
      cajas: Array.from(new Set(idsRendiciones)).join(","),
    });
    const cargar = async () => {
      try {
        const data = await requestJson<MonchisTesoreriaMonto[]>(
          `/api/tesoreria/monchis/conciliaciones/tesoreria?${params.toString()}`,
        );
        if (!cancelado) {
          setMonchisTesoreriaMontos(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        console.warn("No se pudieron obtener los montos conciliados de Monchis", error);
        if (!cancelado) {
          setMonchisTesoreriaMontos([]);
        }
      }
    };
    void cargar();
    return () => {
      cancelado = true;
    };
  }, [idsRendiciones, tieneAcceso]);

  const manejarAccionConciliacion = useCallback(
    async ({
      etapa,
      estado,
      nota,
      rendicionId,
    }: {
      etapa: "supervisor" | "tesoreria";
      estado: "aprobada" | "rechazada" | "pendiente";
      nota?: string;
      rendicionId: number;
    }) => {
      await requestJson(`/api/rendiciones/${rendicionId}/conciliacion/${etapa}`, {
        method: "PUT",
        body: JSON.stringify({ estado, nota }),
      });
      await cargarRendiciones();
    },
    [cargarRendiciones],
  );

  const manejarConciliacionForma = useCallback(
    async ({
      rendicionId,
      etapa,
      claveForma,
      estado,
      nota,
      idcontrolcaja,
      idformaPago,
    }: {
      rendicionId: number;
      etapa: "supervisor" | "tesoreria";
      claveForma: string;
      estado: boolean;
      nota?: string;
      idcontrolcaja?: number | null;
      idformaPago?: number | null;
    }) => {
      await requestJson(`/api/rendiciones/${rendicionId}/conciliacion/formas`, {
        method: "PUT",
        body: JSON.stringify({
          etapa,
          claveForma,
          estado,
          nota,
          idcontrolcaja,
          idformaPago,
        }),
      });
      await cargarRendiciones();
    },
    [cargarRendiciones],
  );

  return (
    <div className="page-content">
      <Container fluid className="px-2 px-md-3">
        <BreadCrumb
          title="Conciliación de caja"
          pageTitle="Módulos"
          description="Conciliación a tres niveles: cajero, supervisor y tesorería."
        />
        <Offcanvas direction="end" isOpen={filtrosVisibles} toggle={toggleFiltros} scrollable>
          <OffcanvasHeader toggle={toggleFiltros}>Filtros de conciliación</OffcanvasHeader>
          <OffcanvasBody>
              <FiltersPanel
                periodoDesde={periodoDesde}
                periodoHasta={periodoHasta}
                onPeriodoDesdeChange={setPeriodoDesde}
                onPeriodoHastaChange={setPeriodoHasta}
                opcionesServidores={opcionesServidores}
                servidorSeleccionado={servidorSeleccionado}
                onServidorChange={setServidorSeleccionado}
              opcionesSucursales={opcionesSucursales}
              sucursalSeleccionada={sucursalSeleccionada}
              onSucursalChange={setSucursalSeleccionada}
              opcionesTurnos={opcionesTurnos}
              turnoSeleccionado={turnoSeleccionado}
              onTurnoChange={setTurnoSeleccionado}
              opcionesCajas={opcionesCajas}
              cajaSeleccionada={cajaSeleccionada}
              onCajaChange={setCajaSeleccionada}
                opcionesCajeros={opcionesCajeros}
                cajeroSeleccionado={cajeroSeleccionado}
                onCajeroChange={setCajeroSeleccionado}
                estadoSeleccionado={estadoSeleccionado}
                estadoOpciones={ESTADO_OPCIONES}
                onEstadoChange={setEstadoSeleccionado}
                opcionesFormasPago={opcionesFormasPago}
                formaPagoSeleccionada={formaPagoSeleccionada}
                onFormaPagoChange={setFormaPagoSeleccionada}
                cargando={cargandoFiltros}
                error={errorFiltros}
                onReload={manejarActualizarFiltros}
              />
          </OffcanvasBody>
        </Offcanvas>

        <StepArrowNav steps={timelineSteps} current={pasoActual} onStepClick={handleStepSelection} />

        <Card>
          <CardBody>
            {tieneAcceso ? (
              <StepLayout
                title={pasoActualStep.label}
                description={pasoDescripcion}
                onBack={retrocederPaso}
                onNext={avanzarPaso}
                disableBack={!puedeRetroceder}
                disableNext={!puedeAvanzar}
              >
                {pasoActualId === "sincronizacion" && (
                  <PlataformasSincronizacionPanel
                    titulo="Sincronización con sistema de gestión a BaconApp"
                    descripcion="Descargá automáticamente las ventas con sus formas de pago hacia BaconApp y mantené los datos listos para cruzarlos con las plataformas sin planillas manuales."
                    verificarEndpoint="/api/tesoreria/importaciones/monchis/conciliaciones/estado"
                    sincronizarEndpoint="/api/tesoreria/importaciones/monchis/conciliaciones"
                    empresaCodigoLabel="Empresa"
                    empresaCodigoPlaceholder="Seleccioná una empresa o dejá todas"
                  />
                )}

                {pasoActualId === "control" && (
                  <>
                    <div className="d-flex align-items-center justify-content-end mb-3">
                      <Button color="primary" outline onClick={() => setFiltrosVisibles(true)}>
                        <i className="ri-filter-3-line me-1" />
                        Filtros
                      </Button>
                    </div>
                    <ControlCajaPanel
                      filasPorFormaPago={filasPorFormaPago}
                      filasReporte={reporte}
                      cargando={cargandoReporte}
                      error={errorReporte}
                      rendicionesPorCaja={rendicionesPorCaja}
                      puedeConciliarSupervisor={puedeConciliarSupervisor}
                      puedeConciliarTesoreria={puedeConciliarTesoreria}
                      onActualizarConciliacion={manejarAccionConciliacion}
                      onActualizarConciliacionForma={manejarConciliacionForma}
                      monchisTesoreriaMontos={monchisTesoreriaMontos}
                      onReload={() => void cargarReporte()}
                      onAgregarFormaPago={handleAgregarFormaPago}
                      onVerFacturas={handleVerFacturas}
                      onEliminarFormaManual={manejarEliminarFormaManual}
                    />
                  </>
                )}

                {pasoActualId === "reportes" && (
                  <RegistroComprasStepper
                    cajasDisponibles={cajasDisponibles}
                    cargandoCajas={cargandoCajasDeposito}
                    errorCajas={errorCajasDeposito}
                    onRecargarCajas={cargarCajasDeposito}
                    sucursalesDisponibles={sucursalesDeposito}
                    cargandoSucursales={cargandoSucursalesDeposito}
                    errorSucursales={errorSucursalesDeposito}
                    onRecargarSucursales={cargarSucursalesDeposito}
                  />
                )}

                {pasoActualId === "depositos" && (
                  <DepositoBoletaForm
                    cajasDisponibles={cajasDisponibles}
                    cargandoCajas={cargandoCajasDeposito}
                    errorCajas={errorCajasDeposito}
                    onReloadCajas={cargarCajasDeposito}
                    sucursalesDisponibles={sucursalesDeposito}
                    cargandoSucursales={cargandoSucursalesDeposito}
                    errorSucursales={errorSucursalesDeposito}
                    onReloadSucursales={cargarSucursalesDeposito}
                    onSalirDeposito={() => setTabActiva("control")}
                  />
                )}

                {pasoActualId === "plataformas" && (
                  <>
                    <div className="mb-4">
                      <Nav tabs className="nav-tabs mb-3 flex-nowrap overflow-auto">
                        {INTEGRACIONES_PLATAFORMAS.map(({ id, titulo }) => (
                          <NavItem key={`plataforma-${id}`}>
                            <NavLink
                              href="#"
                              style={{ cursor: "pointer" }}
                              className={plataformaTabActiva === id ? "active" : ""}
                              onClick={(event) => {
                                event.preventDefault();
                                setPlataformaTabActiva(id);
                              }}
                            >
                              {titulo}
                            </NavLink>
                          </NavItem>
                        ))}
                      </Nav>
                    </div>
                    <TabContent activeTab={plataformaTabActiva} className="pt-4">
                      {INTEGRACIONES_PLATAFORMAS.map(({ id, panelProps }) => (
                        <TabPane tabId={id} key={`subtab-${id}`}>
                          {id === "monchis" ? (
                            <MonchisConciliacionPanel panelProps={panelProps} />
                          ) : (
                            <ConciliarPlataformaPanel {...panelProps} />
                          )}
                        </TabPane>
                      ))}
                    </TabContent>
                  </>
                )}
              </StepLayout>
            ) : (
              <Alert color="warning" className="mb-0">
                No tenés permisos para ver la conciliación de caja.
              </Alert>
            )}
          </CardBody>
        </Card>

        <Modal
          isOpen={modalFacturas.visible}
          toggle={cerrarModalFacturas}
          size="lg"
          centered
        >
          <ModalHeader toggle={cerrarModalFacturas}>
            Facturas del grupo
          </ModalHeader>
          <ModalBody>
            {modalFacturas.contexto ? (
              <>
                <p className="text-muted">
                  {modalFacturas.contexto.sucursal} · {modalFacturas.contexto.turno} · {modalFacturas.contexto.caja} ·{" "}
                  {modalFacturas.contexto.cajero}
                </p>
                {modalFacturasCargando ? (
                  <div className="text-center text-muted py-4">Cargando facturas del grupo…</div>
                ) : modalFacturasError ? (
                  <Alert color="danger">{modalFacturasError}</Alert>
                ) : modalFacturasRegistros.length === 0 ? (
                  <div className="text-center text-muted py-4">
                    No identificamos sincronizaciones para esta fecha. {modalFacturas.contexto?.idcontrolcaja ? (
                      <>
                        Revisá la pestaña{" "}
                        <Button
                          color="link"
                          className="p-0 align-baseline"
                          onClick={() => setTabActiva("sincronizacion")}
                        >
                          Sincronización con sistema de gestión
                        </Button>{" "}
                        para sincronizar
                      </>
                    ) : (
                      "Revisá la sincronización con sistema de gestión."
                    )}
                    .
                  </div>
                ) : (
                  <>
                    <div className="d-flex flex-wrap gap-2 mb-3">
                      {[
                        {
                          id: "todos",
                          label: `Todos (${modalFacturasResumen.total})`,
                          color: "soft-primary",
                          activeColor: "danger",
                        },
                        {
                          id: "activas",
                          label: `Activas (${modalFacturasResumen.activas})`,
                          color: "soft-success",
                          activeColor: "success",
                        },
                        {
                          id: "anuladas",
                          label: `Anuladas (${modalFacturasResumen.anuladas})`,
                          color: "soft-danger",
                          activeColor: "danger",
                        },
                      ].map((tab) => (
                        <Button
                          key={tab.id}
                          size="sm"
                          color={modalFacturasTab === tab.id ? tab.activeColor : tab.color}
                          outline={modalFacturasTab !== tab.id}
                          onClick={() => setModalFacturasTab(tab.id as typeof modalFacturasTab)}
                        >
                          {tab.label}
                        </Button>
                      ))}
                    </div>
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className="form-check form-switch form-switch-primary">
                        <Input
                          type="checkbox"
                          className="form-check-input"
                          id="facturas-agrupadas-switch"
                          checked={modalFacturasAgruparFormas}
                          onChange={(event) => setModalFacturasAgruparFormas(event.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="facturas-agrupadas-switch">
                          Agrupar por forma de pago
                        </label>
                      </div>
                    </div>
                    {modalFacturasAgruparFormas ? (
                      <>
                        <div className="table-responsive">
                          <Table className="align-middle table-sm mb-0">
                            <thead className="table-light">
                              <tr>
                                <th>Forma de pago</th>
                                <th className="text-center">Facturas</th>
                                <th className="text-end">
                                  <div>{sistemaGestionTitulo.titulo}</div>
                                  <div className="text-muted small">{sistemaGestionTitulo.subtitulo}</div>
                                </th>
                                <th className="text-end">
                                  <div>{sincronizadosLabel.titulo}</div>
                                  <div className="text-muted small">{sincronizadosLabel.subtitulo}</div>
                                </th>
                                <th className="text-center">Validación de totales</th>
                                <th className="text-center">Acciones</th>
                              </tr>
                            </thead>
                            <tbody>
                              {modalFacturasPorFormaPago.length === 0 ? (
                                <tr>
                                  <td colSpan={6} className="text-center text-muted py-3">
                                    No hay registros para esta vista.
                                  </td>
                                </tr>
                              ) : (
                                modalFacturasPorFormaPago.map((grupo) => {
                                  const sistema = obtenerMontoSistemaPorForma(grupo.descripcion);
                                  const diferencia =
                                    sistema != null ? sistema - grupo.total : null;
                                  const diffColor =
                                    diferencia == null
                                      ? "secondary"
                                      : Math.abs(diferencia) < 1
                                      ? "success"
                                      : diferencia > 0
                                      ? "warning"
                                      : "danger";
                                  return (
                                    <tr key={grupo.descripcion}>
                                      <td>{grupo.descripcion}</td>
                                      <td className="text-center">{grupo.cantidad}</td>
                                      <td className="text-end">
                                        {sistema != null
                                          ? `${sistema.toLocaleString("es-PY", {
                                              minimumFractionDigits: 0,
                                              maximumFractionDigits: 0,
                                            })} Gs.`
                                          : "—"}
                                      </td>
                                      <td className="text-end">
                                        {grupo.total.toLocaleString("es-PY", {
                                          minimumFractionDigits: 0,
                                          maximumFractionDigits: 0,
                                        })}{" "}
                                        Gs.
                                      </td>
                                      <td className="text-center">
                                        {diferencia == null ? (
                                          <span className="text-muted">—</span>
                                        ) : (
                                          <Badge color={diffColor}>
                                            {diferencia > 0 ? "+" : ""}
                                            {diferencia.toLocaleString("es-PY", {
                                              minimumFractionDigits: 0,
                                              maximumFractionDigits: 0,
                                            })}{" "}
                                            Gs.
                                          </Badge>
                                        )}
                                      </td>
                                      <td className="text-center">
                                        <Button
                                          size="sm"
                                          color="soft-primary"
                                          onClick={() => abrirModalFacturasForma(grupo.descripcion, grupo.registros)}
                                        >
                                          Ver facturas
                                        </Button>
                                      </td>
                                    </tr>
                                  );
                                })
                              )}
                            </tbody>
                          </Table>
                        </div>
                        <div className="text-end mt-2 text-muted d-flex flex-column align-items-end gap-2">
                          <div>
                            <div className="fw-semibold">{sistemaGestionTitulo.titulo}</div>
                            <div className="text-muted small">{sistemaGestionTitulo.subtitulo}</div>
                            <strong>
                              {totalSistemaAgrupado.toLocaleString("es-PY", {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0,
                              })}{" "}
                              Gs.
                            </strong>
                          </div>
                          <div>
                            <div className="fw-semibold">{sincronizadosLabel.titulo}</div>
                            <div className="text-muted small">{sincronizadosLabel.subtitulo}</div>
                            <strong>
                              {totalFacturasAgrupado.toLocaleString("es-PY", {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0,
                              })}{" "}
                              Gs.
                            </strong>
                          </div>
                          <div>
                            <div className="fw-semibold">Validación de totales</div>
                            <Badge
                              color={
                                Math.abs(totalSistemaAgrupado - totalFacturasAgrupado) < 1
                                  ? "success"
                                  : totalSistemaAgrupado - totalFacturasAgrupado > 0
                                  ? "warning"
                                  : "danger"
                              }
                            >
                              {totalSistemaAgrupado - totalFacturasAgrupado > 0 ? "+" : ""}
                              {(totalSistemaAgrupado - totalFacturasAgrupado).toLocaleString("es-PY", {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0,
                              })}{" "}
                              Gs.
                            </Badge>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="table-responsive">
                        <Table className="align-middle table-nowrap mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>Factura / Recibo</th>
                              <th>Fecha</th>
                              <th>Cliente</th>
                              <th className="text-center">Tipo</th>
                              <th className="text-end">Monto</th>
                            </tr>
                          </thead>
                          <tbody>
                            {modalFacturasFiltradas.length === 0 ? (
                              <tr>
                                <td colSpan={4} className="text-center text-muted py-3">
                                  No hay registros para esta vista.
                                </td>
                              </tr>
                            ) : (
                              modalFacturasFiltradas.map((fila) => (
                                <tr key={`${fila.nromovimiento}-${fila.formaPago}-${fila.tipoFactura}`}>
                                  <td>{fila.nromovimiento ?? "Sin dato"}</td>
                                  <td>{formatFechaCorta(fila.fechaDia)}</td>
                                  <td>{fila.persona ?? "Sin registro"}</td>
                                  <td className="text-center">
                                    <Badge
                                      color={
                                        String(fila.tipoFactura ?? "").toUpperCase() === "ANULADA"
                                          ? "danger"
                                          : "secondary"
                                      }
                                    >
                                      {fila.tipoFactura || "Sin tipo"}
                                    </Badge>
                                  </td>
                                  <td className="text-end">
                                    {fila.monto.toLocaleString("es-PY", {
                                      minimumFractionDigits: 0,
                                      maximumFractionDigits: 0,
                                    })}{" "}
                                    Gs.
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </Table>
                        <div className="text-end mt-2 text-muted">
                          <div className="fw-semibold">{sincronizadosLabel.titulo}</div>
                          <div className="text-muted small">{sincronizadosLabel.subtitulo}</div>
                          <strong>
                            {(modalFacturasTab === "todos"
                              ? modalFacturasResumen.sumatorias.todos
                              : modalFacturasTab === "activas"
                              ? modalFacturasResumen.sumatorias.activas
                              : modalFacturasResumen.sumatorias.anuladas
                            ).toLocaleString("es-PY", {
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            })}{" "}
                            Gs.
                          </strong>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            ) : (
              <p className="text-muted mb-0">Seleccioná un grupo para ver sus facturas.</p>
            )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={cerrarModalFacturas}>
            Cerrar
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={modalFacturasFormaDetalle.visible} toggle={cerrarModalFacturasForma} size="lg" centered>
        <ModalHeader toggle={cerrarModalFacturasForma}>
          Facturas - {modalFacturasFormaDetalle.forma ?? "Sin forma"}
        </ModalHeader>
        <ModalBody>
          {modalFacturasFormaDetalle.registros.length === 0 ? (
            <div className="text-center text-muted py-4">No hay facturas registradas para esta forma de pago.</div>
          ) : (
            <div className="table-responsive">
              <Table className="align-middle table-sm mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Factura / Recibo</th>
                    <th>Fecha</th>
                    <th>Cliente</th>
                    <th className="text-center">Tipo</th>
                    <th className="text-end">Monto</th>
                  </tr>
                </thead>
                <tbody>
                  {modalFacturasFormaDetalle.registros.map((fila) => (
                    <tr key={`${fila.nromovimiento}-${fila.persona}-${fila.tipoFactura}`}>
                      <td>{fila.nromovimiento ?? "Sin dato"}</td>
                      <td>{formatFechaCorta(fila.fechaDia)}</td>
                      <td>{fila.persona ?? "Sin registro"}</td>
                      <td className="text-center">
                        <Badge
                          color={
                            String(fila.tipoFactura ?? "").toUpperCase() === "ANULADA" ? "danger" : "secondary"
                          }
                        >
                          {fila.tipoFactura || "Sin tipo"}
                        </Badge>
                      </td>
                      <td className="text-end">
                        {fila.monto.toLocaleString("es-PY", {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })}{" "}
                        Gs.
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <div className="text-end mt-2 text-muted">
                Total forma ({modalFacturasFormaDetalle.forma ?? "—"}):{" "}
                <strong>
                  {modalFacturasFormaDetalle.registros
                    .reduce((acc, fila) => acc + Number(fila.monto ?? 0), 0)
                    .toLocaleString("es-PY", {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })}{" "}
                  Gs.
                </strong>
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={cerrarModalFacturasForma}>
            Cerrar
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={modalFormaPago.visible} toggle={cerrarModalFormaPago} centered>
        <ModalHeader toggle={cerrarModalFormaPago}>Agregar forma de pago</ModalHeader>
          <ModalBody>
            {modalFormaPago.contexto ? (
              <>
                <p className="text-muted mb-3">
                  {modalFormaPago.contexto.sucursal} · {modalFormaPago.contexto.turno} · {modalFormaPago.contexto.caja}
                </p>
                <div className="d-flex flex-wrap align-items-center justify-content-between text-muted small mb-2">
                  <span>
                    Catálogo
                    {" "}
                    {formasPagoDisponibles.length > 0 ? "disponible para este perfil." : "pendiente de actualización."}
                  </span>
                  <Button color="light" size="sm" disabled={formasPagoCargando} onClick={() => void cargarFormasPago()}>
                    {formasPagoCargando ? "Actualizando..." : "Actualizar lista"}
                  </Button>
                </div>
                {formasPagoError && (
                  <Alert color="danger">
                    {formasPagoError}
                  </Alert>
                )}
                <div className="border rounded">
                  {formasPagoCargando ? (
                    <div className="text-center text-muted py-4">Cargando formas de pago...</div>
                  ) : formasPagoActivas.length === 0 ? (
                    <div className="text-center text-muted py-4">No hay formas de pago activas disponibles.</div>
                  ) : (
                    <div className="table-responsive">
                      <Table className="align-middle table-sm mb-0">
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: 40 }} />
                            <th style={{ width: 90, textAlign: "left" }}>ID</th>
                            <th>Descripción</th>
                            <th className="text-end">Estado</th>
                          </tr>
                        </thead>
                        <tbody>
                          {formasPagoActivas.map((forma) => {
                            const descripcionNormalizada = normalizarFormaPagoDescripcion(forma.descripcion);
                            const yaExiste = formasExistentesNormalizadas.has(descripcionNormalizada);
                            const seleccionado = formasPagoSeleccionadasIds.has(forma.id);
                            const rowClass = yaExiste
                              ? "bg-light text-muted"
                              : seleccionado
                                ? "table-active"
                                : undefined;
                            return (
                              <tr
                                key={forma.id}
                                className={rowClass}
                                style={{ cursor: yaExiste ? "not-allowed" : "pointer" }}
                                onClick={() => {
                                  if (yaExiste || formasPagoCargando) return;
                                  setFormasPagoSeleccionadasIds((prev) => {
                                    const siguiente = new Set(prev);
                                    if (siguiente.has(forma.id)) {
                                      siguiente.delete(forma.id);
                                    } else {
                                      siguiente.add(forma.id);
                                    }
                                    return siguiente;
                                  });
                                  setFormasPagoError(null);
                                }}
                              >
                                <td className="text-center">
                                  <Input
                                    type="checkbox"
                                    name="formaPagoSeleccionada"
                                    checked={seleccionado}
                                    disabled={yaExiste}
                                    onChange={() => {
                                      if (yaExiste || formasPagoCargando) return;
                                      setFormasPagoSeleccionadasIds((prev) => {
                                        const siguiente = new Set(prev);
                                        if (siguiente.has(forma.id)) {
                                          siguiente.delete(forma.id);
                                        } else {
                                          siguiente.add(forma.id);
                                        }
                                        return siguiente;
                                      });
                                      setFormasPagoError(null);
                                    }}
                                  />
                                </td>
                                <td className="text-muted">#{forma.id}</td>
                                <td>
                                  <div className="fw-semibold text-body">{forma.descripcion || "Sin descripción"}</div>
                                </td>
                                <td className="text-end">
                                  {yaExiste ? (
                                    <Badge color="soft-success" className="fw-semibold">
                                      Ya agregada
                                    </Badge>
                                  ) : seleccionado ? (
                                    <Badge color="soft-primary" className="fw-semibold">
                                      Seleccionada
                                    </Badge>
                                  ) : (
                                    <span className="text-muted">Disponible</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <p className="text-muted mb-0">Seleccioná un grupo antes de cargar una forma de pago.</p>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="light" onClick={cerrarModalFormaPago}>
              Cancelar
            </Button>
            <Button color="primary" onClick={confirmarAgregarFormaPago} disabled={!modalFormaPago.contexto || formasPagoSeleccionadasIds.size === 0 || formasPagoCargando}>
              Agregar
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
};

export default ConciliacionCajaPage;
